import { type JSX } from 'react';

import { consumerApi } from './api';
import { EarnBadge, ErrorState, LoadingState, PriceTag, fulfilmentLabel, useEarn } from './components';
import { usePageMeta } from './page-meta';
import { AppLink } from './router';
import { useResource } from './useResource';

/**
 * One offer's page.
 *
 * ## The price here is the authoritative one
 *
 * It arrives already resolved by Phase 17's single effective-price path, with
 * any promotion applied. Nothing on this page recalculates it — a second
 * calculation could disagree with the number shown on the search result the
 * customer clicked, and they would reasonably expect the first.
 *
 * ## There is no "add to basket"
 *
 * Cart, checkout and payment are later phases. A button that looked like it
 * bought something and did not would be worse than its absence, so the page
 * ends at the offer and says where it is available.
 */
export function OfferPage({ listingId }: { readonly listingId: string }): JSX.Element {
  const resource = useResource(`offer:${listingId}`, (signal) => consumerApi.offer(listingId, signal));
  const earn = useEarn([listingId]);

  const offer = resource.status === 'loaded' ? resource.data : undefined;

  usePageMeta({
    title: offer?.title ?? (resource.status === 'error' ? 'Not found' : 'Loading'),
    ...(offer?.summary !== undefined ? { description: offer.summary } : {}),
    ...(offer !== undefined ? { canonicalPath: `/offers/${offer.id}` } : {}),
    // A real offer is a real page worth indexing. Anything else — loading, an
    // error, a listing that is not published — is not, and a single-page app
    // serves 200 for all of them unless this says otherwise.
    noindex: offer === undefined,
  });

  if (resource.status === 'loading') return <LoadingState label="Loading this offer…" />;

  if (resource.status === 'error') {
    return (
      <div className="jt-page jt-container">
        {resource.notFound ? (
          <>
            <h1 className="jt-page__title">We could not find that.</h1>
            <p>
              It may have been withdrawn, or the link may be wrong.{' '}
              <AppLink className="jt-link" to="/search">
                Search the marketplace
              </AppLink>
              .
            </p>
          </>
        ) : (
          <ErrorState message={resource.message} onRetry={resource.reload} />
        )}
      </div>
    );
  }

  const detail = resource.data;

  return (
    <article className="jt-page jt-container jt-offer">
      <p className="jt-offer__kind">{detail.kind === 'SERVICE' ? 'Service' : 'Product'}</p>
      <h1 className="jt-page__title">{detail.title}</h1>

      {detail.brandName !== undefined ? <p className="jt-offer__brand">{detail.brandName}</p> : null}

      <p className="jt-offer__seller">
        Offered by{' '}
        <AppLink className="jt-link" to={`/storefronts/${detail.storefront.slug}`}>
          {detail.storefront.name}
        </AppLink>
      </p>

      <PriceTag price={detail.price} />
      <EarnBadge units={earn.get(listingId)} />

      {detail.restriction.prescriptionRequired ? (
        <p className="jt-note jt-note--restriction">
          A prescription is required before this can be supplied.
        </p>
      ) : detail.restriction.consultationRequired ? (
        <p className="jt-note jt-note--restriction">
          A consultation is required before this can be supplied.
        </p>
      ) : detail.restriction.ageRestricted ? (
        <p className="jt-note jt-note--restriction">This is age restricted.</p>
      ) : null}

      {/* Services only, and only when the diary answered. A product never gets
          one, and an unreachable diary renders nothing rather than a guess. */}
      {detail.bookability !== undefined ? (
        <p className={detail.bookability.bookable ? 'jt-note jt-note--available' : 'jt-note'}>
          {detail.bookability.bookable
            ? detail.bookability.nextAvailableAt === undefined
              ? 'Appointments are available.'
              : `Next appointment ${new Date(detail.bookability.nextAvailableAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}.`
            : 'No appointments are available at the moment.'}
        </p>
      ) : null}

      {detail.description !== undefined ? (
        <section className="jt-offer__section" aria-labelledby="jt-offer-about">
          <h2 id="jt-offer-about">About this {detail.kind === 'SERVICE' ? 'service' : 'product'}</h2>
          <p>{detail.description}</p>
        </section>
      ) : null}

      {detail.fulfilmentModes.length > 0 ? (
        <section className="jt-offer__section" aria-labelledby="jt-offer-fulfilment">
          <h2 id="jt-offer-fulfilment">How you can get this</h2>
          <ul className="jt-chips">
            {detail.fulfilmentModes.map((mode) => (
              <li key={mode} className="jt-chip">
                {fulfilmentLabel(mode)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.locations.length > 0 ? (
        <section className="jt-offer__section" aria-labelledby="jt-offer-where">
          <h2 id="jt-offer-where">Where</h2>
          <ul className="jt-locations">
            {detail.locations.map((location) => (
              <li key={location.id}>
                {/* Town and postcode only — never coordinates, a street or a
                    service area. A provider's catchment is their business. */}
                {[location.town, location.postcode].filter(Boolean).join(', ')}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
